import * as React from 'react'

import { isGemPackProduct, ProductContent, ProductV2 } from '@common'
import cx from 'classnames'
import Image from 'next/image'

import { useGameImages } from '@/components/games/images'
import { ImageWithFallback } from '@/features/storefronts/_storeBase/assetRendering/ImageWithFallback'
import { useFormattedAmount } from '@/features/storefronts/_storeBase/card/utils'
import { Flare } from '@/features/storefronts/_storeBase/flare/Flare'
import { StoreTypography } from '@/features/storefronts/_storeBase/typography/StoreTypography'
import { useAssetColor } from '@/features/storefronts/brawlstars/bundle/helpers'
import { clashResources } from '@/features/storefronts/clashofclans/contants'
import { isCosmetic, isScenery, isSkin } from '@/features/storefronts/productFilters'
import { useTranslatable } from '@/localization/useTranslatable'
import { isProductImageEmpty, resolveProductImage } from '@/utils/resolveProductImage'

import { DisplayAsset } from '../../../../../../assetRendering/DisplayAsset'

import BgFlare from 'assets/images/storefronts/clashofclans/shop_item_bg_flare.png'

import styles from './ClashOfClansItem.module.scss'
import colors from '../colors.module.scss'

type ItemProps = {
  product: ProductV2
}

type SubItemProps = {
  content: ProductContent
  isStoreSpecial?: boolean
  type?: 'default' | 'large'
}

export const ClashGemPackImage = ({ product }: ItemProps) => {
  const translatable = useTranslatable()
  const { gameImage } = useGameImages('clashofclans')
  const productImage = !isProductImageEmpty(product)
    ? resolveProductImage(product)
    : gameImage('missingProductImageSmall')
  return (
    <div className={cx(styles.ProductImage, styles.GemItem)}>
      <Image
        src={productImage}
        alt={translatable(product.title)}
        style={{ objectFit: 'cover' }}
        fill
        quality={100}
        sizes="200px"
      />
    </div>
  )
}

export const ClashProductImage = ({
  content,
  cosmeticImgSrc,
  isSubItem,
  isSkinOrScenery,
}: {
  content: ProductContent
  cosmeticImgSrc?: string
  isSubItem?: boolean
  isSkinOrScenery?: boolean
}) => {
  const translatable = useTranslatable()
  const title = translatable(content.product.title)

  return (
    <div
      className={cx(styles.ProductImage, {
        [styles.SubItemProductImage]: isSubItem,
        [styles.BiggerProductImage]: isSkinOrScenery,
      })}
    >
      {cosmeticImgSrc ? (
        <ImageWithFallback productImage={cosmeticImgSrc} title={title} />
      ) : (
        <DisplayAsset gameId="clashofclans" content={content} />
      )}
    </div>
  )
}

export function ClashOfClansItem({ product }: ItemProps) {
  const content = product.contents[0]
  let colorStyle = ''
  const colorScheme = useAssetColor(content?.product.id)
  const shownTitle = useFormattedAmount(content.amount)

  const isGemPack = isGemPackProduct(product)
  const isCosmeticOffer = isCosmetic(product)
  const isSkinItem = isSkin(product)
  const isSceneryItem = isScenery(product)
  const cosmeticOfferProductImage = isCosmeticOffer ? product.images[0] : undefined
  const cosmeticImgSrc = cosmeticOfferProductImage

  const isTitleShown = !isSkinItem && !isCosmeticOffer && !isSceneryItem

  const displayClashXmultiplier = !clashResources.includes(content.product.id)
  const isSingleItemOffer = product.contents.length === 1

  // for singleItemOffer colorScheme comes from container
  if (!isSingleItemOffer) {
    colorStyle = colors[colorScheme]
  }

  return (
    <div className={cx(styles.SingleItemCard, colorStyle)}>
      <div className={cx(styles.Flare, { [styles.BiggerFlare]: isSkinItem || isSceneryItem })}>
        <Image src={BgFlare} unoptimized alt="product flare" fill sizes="200px" />
      </div>
      {isGemPack ? (
        <ClashGemPackImage product={product} />
      ) : (
        <ClashProductImage
          content={content}
          cosmeticImgSrc={cosmeticImgSrc}
          isSkinOrScenery={isSkinItem || isSceneryItem}
        />
      )}
      <div className={styles.FooterWrapper}>
        {isTitleShown && (
          <span className={styles.Footer}>
            <StoreTypography tag="h3" style="outlined" textSize="body" className={styles.Title}>
              {shownTitle}
              {displayClashXmultiplier && 'x'}
            </StoreTypography>
          </span>
        )}
      </div>
    </div>
  )
}

export function ClashOfClansSubItem({ content, isStoreSpecial, type }: SubItemProps) {
  const translatable = useTranslatable()
  const shownTitle = useFormattedAmount(content.amount)
  const productTitle = translatable(content.product.title)
  const displayClashXmultiplier = !clashResources.includes(content.product.id)
  const isLargeSubItem = type === 'large'

  const colorScheme = isStoreSpecial ? 'darkBrown' : 'brightPurple'
  const textSize = isLargeSubItem ? 'body' : 'xs'

  return (
    <div className={cx(styles.SubItemCard, colors[colorScheme])}>
      <StoreTypography
        tag="h3"
        style="outlined"
        textSize={textSize}
        className={cx(styles.SubItemTitle, { [styles.LargeSubItem]: isLargeSubItem })}
      >
        {productTitle}
      </StoreTypography>
      <Flare game={'clashofclans'} blurryEllipsis={false} />
      <ClashProductImage content={content} isSubItem={true} />
      <div className={styles.FooterWrapper}>
        <span className={cx(styles.SubItemFooter, { [styles.LargeSubItem]: isLargeSubItem })}>
          <StoreTypography tag="h3" style="outlined" textSize={textSize} className={styles.Title}>
            {shownTitle}
            {displayClashXmultiplier && 'x'}
          </StoreTypography>
        </span>
      </div>
    </div>
  )
}
